import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import {
    Avatar,
    Box,
    ClickAwayListener,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {
    Close,
    ContactSupport,
    ContactSupportOutlined,
    ReportProblem,
    ReportProblemOutlined,
    LocalActivity,
    LocalActivityOutlined,
    LogoutRounded,
    Person,
    PersonOutlined,
    Policy,
    PolicyOutlined,
    Settings,
    SettingsOutlined,
    VerifiedUser,
    VerifiedUserOutlined,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { motion } from "framer-motion";
import { toProfilePath, toSettingsPath } from "../../universal/v2Routes";
import { toSettingsSectionPath } from "../account/accountSettingsNav";
import {
    MembershipBadge,
    resolveMembershipDisplay,
} from "../profile/profileIdentityBadges";
import { MOBILE_NAV_MEDIA } from "./layoutConstants";
import DisplayStatusLabel from "./DisplayStatusLabel";
import BackdropOverlay from "../common/BackdropOverlay.jsx";

const CLOSE_BUTTON_SX = {
    width: "1.9rem",
    height: "1.9rem",
    borderRadius: "0.55rem",
    color: colors.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
};

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const MENU_ICON_SIZE = "1.25rem";
const MENU_ICON_SIZE_MOBILE = "1.15rem";

const MENU_ITEM_SX = {
    position: "relative",
    minHeight: { xs: "2.7rem", md: "2.7rem" },
    px: { xs: "0.55rem", md: "0.8rem" },
    py: { xs: "0.5rem", md: "0.45rem" },
    borderRadius: { xs: "0.45rem", md: "0.55rem" },
    overflow: "hidden",
    color: colors.text.primary,
    transition:
        "all 180ms cubic-bezier(0.22,1,0.36,1)",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `
            linear-gradient(
                90deg,
                rgba(255,255,255,0.04),
                transparent
            )
        `,
        opacity: 0,
        transition: "opacity 180ms ease",
    },
    "&:hover": {
        background: `
            linear-gradient(
                90deg,
                rgba(255,255,255,0.06),
                rgba(255,255,255,0.03)
            )
        `,
        "&::before": {
            opacity: 1,
        },
    },
    "& .MuiListItemIcon-root": {
        color: colors.text.secondary,
        minWidth: { xs: "1.7rem", md: "2rem" },
        transition: "all 180ms ease",
        "& .MuiSvgIcon-root": {
            fontSize: { xs: MENU_ICON_SIZE_MOBILE, md: MENU_ICON_SIZE },
        },
        "& .menu-icon-filled": {
            opacity: 0,
        },
    },
    "&:hover .MuiListItemIcon-root": {
        color: colors.text.secondary,
        transform: "scale(1.08)",
        "& .menu-icon-outlined": {
            opacity: 0,
        },
        "& .menu-icon-filled": {
            opacity: 1,
        },
    },
    "& .MuiListItemText-primary": {
        fontSize: { xs: "0.92rem", md: "0.85rem" },
        fontWeight: 500,
        letterSpacing: "0.03em",
    },
};

const MENU_SECTION_LABEL_SX = {
    px: { xs: "0.65rem", md: "0.9rem" },
    pt: { xs: "0.85rem", md: "0.8rem" },
    pb: { xs: "0.4rem", md: "0.35rem" },
    fontSize: { xs: "0.56rem", md: "0.62rem" },
    fontWeight: 800,
    letterSpacing: "0.16em",
    color: colors.text.secondary,
};

const MENU_FOOTER_SX = {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    mt: { xs: "1.15rem", md: "0.75rem" },
    pt: { xs: "0.7rem", md: "0.6rem" },
    borderTop: "1px solid rgba(255,255,255,0.065)",
    background: "linear-gradient(180deg, rgba(10,10,12,0.15), rgba(10,10,12,0.82))",
};

const LOGOUT_BUTTON_SX = {
    width: "100%",
    minHeight: { xs: "2.4rem", md: "2.75rem" },
    display: "flex",
    alignItems: "center",
    gap: { xs: "0.65rem", md: "0.75rem" },
    px: { xs: "0.7rem", md: "0.85rem" },
    py: "0.45rem",
    color: colors.text.secondary,
    border: "1px solid rgba(255,255,255,0.055)",
    borderRadius: { xs: "0.65rem", md: "0.75rem" },
    background: "linear-gradient(180deg, rgba(255,255,255,0.026), rgba(255,255,255,0.012))",
    font: "inherit",
    fontSize: { xs: "0.78rem", md: "0.84rem" },
    fontWeight: 600,
    letterSpacing: "0.035em",
    cursor: "pointer",
    transition: "all 180ms cubic-bezier(0.22,1,0.36,1)",
    "& svg": {
        fontSize: { xs: "1.05rem", md: "1.2rem" },
        color: colors.text.muted,
        transition: "color 180ms ease, transform 180ms ease",
    },
    "&:hover": {
        color: "#FCA5A5",
        borderColor: "rgba(239,68,68,0.24)",
        background: "linear-gradient(180deg, rgba(239,68,68,0.1), rgba(239,68,68,0.055))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.035), 0 8px 20px rgba(0,0,0,0.2)",
        transform: "translateY(-1px)",
        "& svg": {
            color: colors.state.danger.main,
            transform: "translateX(2px)",
        },
    },
};

const MENU_ITEMS = [
    { sectionLabel: "ACCOUNT" },
    {
        label: "View Profile",
        icon: { outlined: PersonOutlined, filled: Person },
        to: (user, pathname) => toProfilePath(user?._id, pathname),
    },
    {
        label: "Account Settings",
        icon: { outlined: SettingsOutlined, filled: Settings },
        to: (_user, pathname) => toSettingsPath(pathname),
    },
    {
        label: "Subscription",
        icon: {
            outlined: LocalActivityOutlined,
            filled: LocalActivity,
        },
        to: () => toSettingsSectionPath("subscriptions"),
        membershipBadge: true,
    },
    { sectionLabel: "COMPETITIVE" },
    {
        label: "My Reputation",
        icon: { outlined: VerifiedUserOutlined, filled: VerifiedUser },
        to: (user, pathname) => {
            const profilePath = toProfilePath(user?._id, pathname);
            return profilePath
                ? `${profilePath}?tab=reputation`
                : toSettingsPath(pathname);
        },
    },
    { sectionLabel: "SUPPORT" },
    {
        label: "Support Tickets",
        icon: { outlined: ContactSupportOutlined, filled: ContactSupport },
        to: () => toSettingsSectionPath("support-tickets"),
    },
    {
        label: "My Reports",
        icon: { outlined: ReportProblemOutlined, filled: ReportProblem },
        to: () => toSettingsSectionPath("my-reports"),
    },
    {
        label: "Reputation System",
        icon: { outlined: PolicyOutlined, filled: Policy },
        to: () => "/reputation",
    },
];

function MenuItemIcon({ outlined: OutlinedIcon, filled: FilledIcon, isMobile = false }) {
    const iconSize = isMobile ? MENU_ICON_SIZE_MOBILE : MENU_ICON_SIZE;
    const iconSx = {
        position: "absolute",
        fontSize: iconSize,
        transition: "opacity 180ms ease",
    };

    return (
        <Box
            sx={{
                position: "relative",
                width: iconSize,
                height: iconSize,
            }}
        >
            <OutlinedIcon className="menu-icon-outlined" sx={iconSx} />
            <FilledIcon className="menu-icon-filled" sx={iconSx} />
        </Box>
    );
}

MenuItemIcon.propTypes = {
    outlined: PropTypes.elementType.isRequired,
    filled: PropTypes.elementType.isRequired,
    isMobile: PropTypes.bool,
};

function renderMenuEntry(entry, index, context) {
    if (entry.sectionLabel) {
        return (
            <Typography key={`section-${entry.sectionLabel}`} sx={MENU_SECTION_LABEL_SX}>
                {entry.sectionLabel}
            </Typography>
        );
    }
    const to =
        typeof entry.to === "function"
            ? entry.to(context.user, context.pathname)
            : entry.to;

    return (
        <MotionBox
            key={entry.label}
            whileTap={{ scale: 0.98 }}
            initial={{
                opacity: 0,
                x: 14,
            }}
            animate={{
                opacity: 1,
                x: 0,
            }}
            transition={{
                delay: index * 0.028,
                duration: 0.26,
                ease: [0.22, 1, 0.36, 1],
            }}
        >
            <MenuItem
                component={Link}
                to={to}
                sx={MENU_ITEM_SX}
                onClick={context.onClose}
            >
                <ListItemIcon>
                    <MenuItemIcon
                        outlined={entry.icon.outlined}
                        filled={entry.icon.filled}
                        isMobile={context.isMobile}
                    />
                </ListItemIcon>
                <ListItemText>{entry.label}</ListItemText>
                {entry.membershipBadge && context.membershipDisplay ? (
                    <MembershipBadge
                        label={context.membershipDisplay.label}
                        size="compact"
                    />
                ) : null}
            </MenuItem>
        </MotionBox>

    );
}

export default function AvatarContextMenu({
    open,
    anchorEl,
    user,
    avatarSrc,
    countryFlag,
    membership,
    blocked,
    actions,
    onClose,
    onLogout,
}) {
    const location = useLocation();
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const membershipDisplay = resolveMembershipDisplay(
        {
            membershipTier: user?.membershipTier,
            membership: user?.membership || membership,
        },
        { compact: true },
    );

    const menuContext = {
        isMobile,
        membershipDisplay,
        onClose,
        pathname: location.pathname,
        user,
    };

    function handleMenuKeyDown(event) {
        if (event.key === "Escape") {
            event.preventDefault();
            onClose(event);
        }
    }

    function handleLogoutClick(event) {
        if (blocked) {
            event.preventDefault();
            actions?.showUnavailableActionMessage?.();
            return;
        }

        onLogout(event);
    }

    const paperSx = {
        position: "relative",
        overflow: "hidden",
        width: isMobile ? "100%" : "300px",
        height: isMobile ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
        background: `
            linear-gradient(
                180deg,
                #101012 0%,
                #08080A 100%
            )
        `,
        backdropFilter: isMobile ? "none" : "blur(28px)",
        WebkitBackdropFilter: isMobile ? "none" : "blur(28px)",
        border: "1px solid rgba(255,255,255,0.06)",
        boxShadow: `
            0 30px 80px rgba(0,0,0,0.58),
            inset 0 1px 0 rgba(255,255,255,0.05),
            0 0 0 1px rgba(255,255,255,0.02)
        `,
        p: "0.75rem 1rem",
        ...(isMobile
            ? {}
            : {
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: `
                          radial-gradient(
                              circle at top right,
                              rgba(232,163,61,0.12),
                              transparent 38%
                          )
                      `,
                },
            }),
    };

    const menuBody = (
        <Box
            sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                maxHeight: isMobile
                    ? "none"
                    : "calc(100vh - 6rem)",
            }}
            onKeyDown={handleMenuKeyDown}
        >
            <MenuList
                autoFocusItem={open}
                id="menu-list-grow"
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: "0.22rem", md: 0 },
                }}
            >
                <Box
                    sx={
                        isMobile
                            ? {
                                display: "flex",
                                alignItems: "center",
                                gap: "0.65rem",
                                pt: "0.35rem",
                                pb: "1rem",
                                mb: "0.45rem",
                                borderBottom: `1px solid ${colors.border.default}`,
                            }
                            : {
                                p: "0.65rem 0.75rem",
                                background: `
                                    linear-gradient(
                                        180deg,
                                        rgba(20,19,19,0.31),
                                        rgba(11,11,13,0.42)
                                    )
                                `,
                                borderBottom: `1px solid ${colors.border.default}`,
                                mb: "0.25rem",
                                border: "1px solid rgba(255,255,255,0.05)",
                                borderRadius: "8px",
                                backdropFilter: "blur(18px)",
                                boxShadow: `
                                    inset 0 1px 0 rgba(255,255,255,0.04),
                                    0 10px 24px rgba(0,0,0,0.32)
                                `,
                            }
                    }
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: "0.7rem", md: "0.6rem" },
                            width: "100%",
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                flexShrink: 0,
                                width: {
                                    xs: "3.25rem",
                                    md: "3.15rem",
                                },
                                height: {
                                    xs: "3.25rem",
                                    md: "3.15rem",
                                },
                                borderRadius: "50%",
                                padding: {
                                    xs: "1.5px",
                                    md: "2px",
                                },
                                background: `conic-gradient(
                                    from 200deg,
                                    #F2BE63 0%,
                                    #FFD27C 12%,
                                    #D88C25 18%,
                                    rgba(216,140,37,0) 85%,
                                    #F2BE63 100%

                                )`,
                            }}
                        >
                            <Avatar
                                src={avatarSrc}
                                alt={user?.name || "User"}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    border: {
                                        xs: `1.5px solid ${colors.background.base}`,
                                        md: `2px solid ${colors.background.base}`,
                                    },
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: colors.text.primary,
                                    fontSize: {
                                        xs: "0.95rem",
                                        md: "0.9rem",
                                    },
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    textShadow: isMobile
                                        ? "none"
                                        : "0 2px 4px rgba(0, 0, 0, 0.5)",
                                    letterSpacing: "0.5px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                    maxWidth: isMobile
                                        ? "none"
                                        : "7rem",
                                }}
                            >
                                {user?.nickname || user?.name || "User"}
                            </Typography>
                            <Box
                                sx={{
                                    mt: "0.18rem",
                                    flexShrink: 0,
                                    maxWidth: isMobile
                                        ? "none"
                                        : "9rem",
                                }}
                            >
                                <DisplayStatusLabel
                                    displayPresence={user?.displayPresence}
                                />
                            </Box>
                        </Box>
                        {countryFlag && !isMobile ? (
                            <Box
                                component="img"
                                src={countryFlag}
                                alt={user?.country || "Country"}
                                sx={{
                                    width: "1.4rem",
                                    height: "1.05rem",
                                    borderRadius: "2px",
                                    objectFit: "cover",
                                }}
                            />
                        ) : null}
                        {isMobile ? (
                            <IconButton
                                onClick={onClose}
                                aria-label="Close dialog"
                                sx={{
                                    ...CLOSE_BUTTON_SX,
                                    flexShrink: 0,
                                    ml: "auto",
                                }}
                            >
                                <Close sx={{ fontSize: "0.95rem" }} />
                            </IconButton>
                        ) : null}
                    </Box>
                </Box>

                {MENU_ITEMS.map((entry, index) =>
                    renderMenuEntry(entry, index, menuContext),
                )}
            </MenuList>
            <Box sx={MENU_FOOTER_SX}>
                <Box
                    component="button"
                    type="button"
                    aria-label="Logout"
                    aria-disabled={blocked || undefined}
                    onClick={handleLogoutClick}
                    sx={{
                        ...LOGOUT_BUTTON_SX,
                        ...(blocked
                            ? { opacity: 0.48, cursor: "not-allowed" }
                            : {}),
                    }}
                >
                    <LogoutRounded />
                    Log out
                </Box>
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <BackdropOverlay
                open={open}
                onClick={onClose}
                zIndex={10000}
                sx={{ alignItems: "stretch" }}
                contentSx={{
                    width: "100%",
                    maxWidth: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <MotionPaper
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        duration: 0.28,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    sx={paperSx}
                >
                    {menuBody}
                </MotionPaper>
            </BackdropOverlay>
        );
    }

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            role={undefined}
            placement="bottom-end"
            sx={{ zIndex: 10000 }}
            disablePortal
            modifiers={[
                {
                    name: "offset",
                    options: {
                        offset: [0, 4],
                    },
                },
            ]}
        >
            {({ placement }) => (
                <MotionPaper
                    initial={{
                        opacity: 0,
                        y: -14,
                        scale: 0.96,
                        filter: "blur(10px)",
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        filter: "blur(0px)",
                    }}
                    exit={{
                        opacity: 0,
                        y: -10,
                        scale: 0.98,
                        filter: "blur(8px)",
                    }}
                    transition={{
                        duration: 0.34,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    sx={{
                        ...paperSx,
                        transformOrigin:
                            placement === "bottom-end"
                                ? "top right"
                                : "top left",
                    }}
                >
                    <ClickAwayListener onClickAway={onClose}>
                        {menuBody}
                    </ClickAwayListener>
                </MotionPaper>
            )}
        </Popper>
    );
}

AvatarContextMenu.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    user: PropTypes.object,
    avatarSrc: PropTypes.string,
    countryFlag: PropTypes.string,
    membership: PropTypes.object,
    blocked: PropTypes.bool,
    actions: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onLogout: PropTypes.func.isRequired,
};
